import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EID } from '@/lib/element-ids.registry'
import { useStartListingAudit } from '@/hooks/useListingBuilder'
import { useBrands } from '@/hooks/useBrands'
import { BulletListEditor } from '@/components/v2/tools/amazon-listing/BulletListEditor'
import { OptionalFieldsGrid } from '@/components/v2/tools/amazon-listing/OptionalFieldsGrid'
import { ImageUploadGrid, type UploadedImage } from '@/components/v2/tools/amazon-listing/ImageUploadGrid'

import type { RegisterSubmit, SourceSubmitStatus } from './sourceSubmit'

const INPUT_CLS =
  'w-full px-3 py-2 bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500'
const LABEL_CLS = 'block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1'

function BrandInput({
  value,
  onChange,
  brands,
}: {
  value: string
  onChange: (v: string) => void
  brands: { id: string; name: string }[]
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const filtered = value
    ? brands.filter((b) => b.name.toLowerCase().includes(value.toLowerCase()))
    : brands

  return (
    <div ref={ref} className="relative">
      <label className={LABEL_CLS}>{t('amazonListing.manual.brand')}</label>
      <input id={EID['listing-builder-manual-brand-input']} value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={t('amazonListing.manual.brandPlaceholder')} className={INPUT_CLS} />
      {open && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-40 overflow-y-auto rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-lg">
          {filtered.map((b) => (
            <button key={b.id} type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(b.name); setOpen(false) }}
              className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-800 dark:text-neutral-200">
              {b.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function ListingManualTab({
  onStarted,
  mode,
  registerSubmit,
  onStatusChange,
}: {
  onStarted: (jobId: string) => void
  mode?: 'small' | 'standard' | 'custom'
  registerSubmit?: RegisterSubmit
  onStatusChange?: (status: SourceSubmitStatus) => void
}) {
  const { t } = useTranslation()
  const { data: brands } = useBrands()
  const [title, setTitle] = useState('')
  const [brand, setBrand] = useState('')
  const [bullets, setBullets] = useState([''])
  const [description, setDescription] = useState('')
  const [gtin, setGtin] = useState('')
  const [sku, setSku] = useState('')
  const [price, setPrice] = useState('')
  const [category, setCategory] = useState('')
  const [images, setImages] = useState<UploadedImage[]>([])
  const { mutate, isPending, error } = useStartListingAudit()

  const canSubmit = title.trim().length > 0 && !isPending

  const handleSubmit = () => {
    if (!canSubmit) return
    mutate(
      {
        source_ref: title.trim(),
        source_type: 'manual',
        title: title.trim(),
        bullets: bullets.filter(Boolean),
        description: description.trim(),
        brand: brand.trim(),
        market_tld: 'de',
        image_asset_ids: images.filter((img) => img.assetUrl).map((img) => img.assetUrl),
        mode,
        ...(gtin && { gtin }),
        ...(sku && { sku }),
        ...(price && { price }),
        ...(category && { category }),
      },
      { onSuccess: (r) => onStarted(r.job_id) },
    )
  }

  useEffect(() => {
    registerSubmit?.(handleSubmit)
  })
  useEffect(() => {
    onStatusChange?.({
      canSubmit,
      isPending,
      submitId: EID['listing-builder-manual-submit-btn'],
      labelKey: 'listingBuilder.source.start',
    })
  }, [canSubmit, isPending, onStatusChange])

  return (
    <div className="space-y-4">
      <div>
        <label className={LABEL_CLS}>{t('listingBuilder.source.manual.title')} *</label>
        <input id={EID['listing-builder-manual-title-input']} value={title}
          onChange={(e) => setTitle(e.target.value)} className={INPUT_CLS} />
      </div>
      <BrandInput value={brand} onChange={setBrand} brands={brands ?? []} />
      <BulletListEditor bullets={bullets} onChange={setBullets} inputClassName={INPUT_CLS} />
      <div>
        <label className={LABEL_CLS}>{t('listingBuilder.source.manual.description')}</label>
        <textarea id={EID['listing-builder-manual-description-input']} rows={3} value={description}
          onChange={(e) => setDescription(e.target.value)} className={INPUT_CLS} />
      </div>
      <OptionalFieldsGrid gtin={gtin} sku={sku} price={price} category={category}
        onGtinChange={setGtin} onSkuChange={setSku} onPriceChange={setPrice}
        onCategoryChange={setCategory} inputClassName={INPUT_CLS} />
      <ImageUploadGrid images={images} onChange={setImages} />

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{t('listingBuilder.source.error')}</p>
      )}
    </div>
  )
}
